import { Injectable, inject } from '@angular/core';
import { skip } from 'rxjs';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { UserService } from 'src/app/services/user/user.service';

/**
 * Registers this device's push token against the signed-in user, so the backend
 * (crewdle-sdk-firebase) has someone to send FCM notifications to.
 *
 * The mobile shell (`../mobile`) already acquires a native push token — APNs on iOS,
 * FCM on Android — and exposes it at `window.crewdleShell.push`. It deliberately does
 * NOT bind that token to a Firebase uid: that requires an ID token and Firestore rules
 * the shell has no access to. This service is the connect-side half of that contract.
 *
 * Capacitor only. The Electron desktop shell has no push token, and the web build has
 * no `window.crewdleShell` at all.
 */
@Injectable({ providedIn: 'root' })
export class PushRegistrationService {

  private readonly platform = inject(PlatformService);
  private readonly user = inject(UserService);
  private readonly firebase = inject(FirebaseService);

  private started = false;
  /** Last device token registered (re-registered in a new region). */
  private lastToken: string | null = null;

  /** True once this device has a push token: the server then notifies it, so no local notification is needed. */
  public get registered(): boolean {
    return this.lastToken !== null;
  }

  /**
   * Ask the shell for push permission and register whatever token results.
   *
   * Contextual by design — call this once the user is actually signed in and using
   * the app (mirrors the local-notifications permission timing in
   * messaging-listener.service.ts), never at boot: iOS grants exactly one prompt.
   */
  public async start(): Promise<void> {
    if (this.started || !this.platform.is('capacitor')) {
      return;
    }
    this.started = true;

    const push = this.resolvePush();
    if (!push) {
      return;
    }

    // The shell may already hold a token from a previous grant (initPush() at boot
    // registers silently if permission was already granted) and will re-announce it
    // on every 'registration' event, not just this session's first one.
    // Data residency: device tokens live in the ACTIVE ORG's regional project
    // (its functions send the pushes) — register the token again whenever the
    // org handles move to another region.
    this.firebase.region$.pipe(skip(1)).subscribe(() => {
      if (this.lastToken) {
        void this.registerToken(this.lastToken);
      }
    });

    window.addEventListener('crewdle:pushToken', (event) => {
      const token = (event as CustomEvent<{ token: string }>).detail?.token;
      if (token) {
        void this.registerToken(token);
      }
    });

    try {
      const permission = await push.enable();
      if (permission !== 'granted') {
        return;
      }
      const token = push.getToken();
      if (token) {
        await this.registerToken(token);
      }
    } catch {
      // Best-effort: no shell, or the user declined. Not an error.
    }
  }

  private async registerToken(token: string): Promise<void> {
    this.lastToken = token;
    const uid = this.user.user?.uid;
    if (!uid) {
      return;
    }
    const platform = this.platform.is('ios') ? 'ios' : 'android';
    await this.firebase.firestore.docSet(
      `users/${uid}/devices/${token}`,
      {
        token,
        platform,
        updatedAt: this.firebase.firestore.serverTimestamp(),
      },
      { merge: true },
    ).catch(() => {
      // Best-effort: a denied write here must not break sign-in or messaging.
    });
  }

  private resolvePush(): ShellPushApi | null {
    const shell = (window as unknown as { crewdleShell?: { push?: ShellPushApi } }).crewdleShell;
    return shell?.push ?? null;
  }
}

/** The part of `window.crewdleShell` this service touches (see ../mobile/shell/src/push.ts). */
interface ShellPushApi {
  enable(): Promise<string>;
  getToken(): string | null;
}
